import { useQuery } from "@tanstack/react-query";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

// Roles are now managed in the DB (felix_wins.access_control) and returned
// by /api/me. No hardcoded email lists needed on the frontend.

// Grab the embed key from the URL once so every /api call can forward it.
export const EMBED_KEY = typeof window !== "undefined"
  ? new URLSearchParams(window.location.search).get("embed_key")
  : null;

// Global fetch interceptor: automatically attach the embed key header to all
// same-origin /api/* requests so every endpoint recognises the embed session.
if (EMBED_KEY) {
  const _origFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === "string" ? input : input instanceof Request ? input.url : "";
    if (url.startsWith("/api/") || url.startsWith(window.location.origin + "/api/")) {
      init = init || {};
      const headers = new Headers(init.headers);
      headers.set("x-embed-key", EMBED_KEY);
      init.headers = headers;
    }
    return _origFetch.call(this, input, init);
  };
}

/** Build a navigation URL preserving embed_key and embed params across page transitions. */
export function buildNavUrl(overrides: Record<string, string | null> = {}): string {
  const p = new URLSearchParams();
  // Carry over embed params from the current URL.
  if (EMBED_KEY) { p.set("embed_key", EMBED_KEY); p.set("embed", "1"); }
  // Keep the localhost escape hatches (main.tsx) so dev nav stays on the same surface.
  if (typeof window !== "undefined") {
    const cur = new URLSearchParams(window.location.search);
    for (const k of ["wins", "board"]) if (cur.get(k) === "1") p.set(k, "1");
  }
  for (const [k, v] of Object.entries(overrides)) {
    if (v != null) p.set(k, v); else p.delete(k);
  }
  return "?" + p.toString();
}

export function useAuth() {
  const { data, isLoading } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: async (): Promise<{ user: AuthUser | null; sheetUrl: string | null; roles: string[] }> => {
      const headers: Record<string, string> = {};
      if (EMBED_KEY) headers["x-embed-key"] = EMBED_KEY;
      const res = await fetch("/api/me", { headers });
      if (!res.ok) return { user: null, sheetUrl: null, roles: [] };
      return res.json();
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const roles = new Set(data?.roles ?? []);

  return {
    user: data?.user ?? null,
    isLoading,
    isAuthenticated: !!data?.user,
    isAdmin: roles.has("admin"),
    canCreateClips: roles.has("clip_creator"),
    canTagVisibility: roles.has("visibility_tagger"),
    canCurate: roles.has("curator") || roles.has("admin"),
    sheetUrl: data?.sheetUrl ?? null,
  };
}
